// frontend/src/components/upsell/UpgradePill.js

import React from "react";
import { Box, ListItemText, MenuItem, Typography } from "@mui/material";

import {
  AMOUNT_RUN_SX,
  leadWord,
  drawerRowSx,
  pillHitAreaSx,
  pillSx,
  splitAmount,
  textDirection,
} from "./upsellStyles";

/**
 * The navbar upgrade affordance, in both of its shapes.
 *
 * PRESENTATIONAL ONLY. Navbar keeps the gating (`showUpgradePill`), the label
 * memo, the offer read and every analytics event; it mounts these and passes a
 * finished label. That split is what lets the chip be rendered with no auth
 * session behind it, and it is why the chip carries no colour of its own -
 * `pillSx`/`drawerRowSx` own the skin.
 *
 * The label is rendered through `splitAmount`, which SLICES the approved string
 * rather than rewriting it: the sentence stays byte-identical and in order, and
 * only the digit run is set in tabular 700. The amount is the reason the chip
 * exists and it used to be indistinguishable from the words beside it.
 */

/**
 * The label, with its number set as a number AND PROTECTED.
 *
 * The chip has to be able to shrink (a flex item that cannot shrink pushes its
 * neighbours out of the container instead of being clipped), but the amount is
 * the one part that must never be clipped - a chip reading "שדרג - חיסכון עד"
 * with the number cut off is worse than no chip. So the label is a flex row of
 * runs: everything BEFORE the number can ellipsise, the number and everything
 * after it (the currency word) cannot.
 *
 * The string is still only SLICED - the approved copy is rendered in full and
 * in order whenever there is room, which at every width the product actually
 * shows this chip there is.
 *
 * ORDER IS THE CALLER'S JOB, and it is not optional. These runs are FLEX ITEMS,
 * so the reading order they are laid out in comes from the flex container's
 * `direction` - not from the bidi algorithm, which never crosses a flex item
 * boundary. The container therefore carries an explicit `dir` taken from the
 * label itself (`textDirection`). Rendering these runs inside anything that
 * does not is the bug this file was fixed for: the amount ends up at the wrong
 * end of the sentence.
 */
const AmountLabel = React.memo(function AmountLabel({ label }) {
  const runs = splitAmount(label);
  if (runs.length <= 1) return <>{label}</>;
  const firstAmount = runs.findIndex((run) => run.isAmount);
  return (
    <>
      {runs.map((run, index) => {
        const fixed = firstAmount !== -1 && index >= firstAmount;
        return (
          <Box
            key={index}
            component="span"
            // The digit island declares its own direction. `unicode-bidi` in
            // `AMOUNT_RUN_SX` is ignored on a blockified flex item; the
            // attribute is what actually binds, and it keeps a grouped amount
            // ("1,200") whole if the copy ever carries one.
            {...(run.isAmount ? { dir: "ltr" } : null)}
            data-amount-run={run.isAmount ? "1" : undefined}
            sx={{
              ...(run.isAmount ? AMOUNT_RUN_SX : null),
              whiteSpace: "pre",
              ...(fixed
                ? { flexShrink: 0 }
                : {
                    flexShrink: 1,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }),
            }}
          >
            {run.text}
          </Box>
        );
      })}
    </>
  );
});

/**
 * The compact chip's visible text: the WHOLE label when the caller says the
 * label is already a short form (`whole`), else its lead word.
 *
 * WHY AN EXPLICIT FLAG, NOT A LENGTH RULE. The friend-code chip ("20% הנחת
 * חבר" / "20% friend discount") IS the short form - its lead word would be
 * the bare "20%", which reads as a price tag rather than a discount - so the
 * navbar asks for it whole. A character-count threshold decided this before
 * and silently cut the English label to "20%" (19 characters, over the
 * estimate). Both whole labels were rendered in the real navbar at 360px
 * (headless Chrome, Hebrew and English, 20% and 30%) and fit beside the
 * wordmark and the menu button. CD-01 and the plain "שדרג" keep the lead-verb
 * slice described below.
 *
 * @param {string} label
 * @param {boolean} [whole]
 * @returns {string}
 */
export const compactChipText = (label, whole = false) => {
  if (typeof label !== "string" || !label) return "";
  return whole ? label : leadWord(label);
};

/**
 * Desktop chip. Rendered from `md` up by the navbar; below that the drawer row
 * is the affordance.
 *
 * COMPACT is the phone app-bar form. At 360 the bar has ~159px between the
 * wordmark and the hamburger, and the full approved label needs 158px in
 * Hebrew and 193px in English before gaps - so it does not fit, in either
 * language. The compact chip therefore shows the LEAD VERB alone ("שדרג" /
 * "Upgrade") while the full approved sentence stays as the control's
 * accessible name. It is a SLICE of the approved string, never a reworded
 * short version, and because the visible text is a substring of the accessible
 * name it also satisfies WCAG 2.5.3 (Label in Name).
 *
 * The verb, not the amount (owner, 2026-09-20): a bare "31 ש"ח" in the app bar
 * reads as a price rather than a saving, and the words that would make it a
 * saving are the ones there is no room for.
 *
 * The alternative - collapsing the wordmark to a mark at xs to make room for
 * the whole sentence - was measured (it frees ~72px, enough for both
 * languages) and REJECTED: it would change the bar for every phone user
 * including those with no offer, which is not additive, and the wordmark would
 * visibly change at the moment the chip appeared.
 *
 * @param {object} p
 * @param {string} [p.variant]
 * @param {string} p.label      pre-localised, CD-01 or the plain "שדרג"
 * @param {boolean} [p.hasOffer] drives the skin, not the label
 * @param {boolean} [p.compact]  the phone app-bar form: the lead verb, painted
 *   at PILL_PAINTED_HEIGHT inside a 44px transparent hit area
 * @param {boolean} [p.compactWhole] compact only: the label is already a short
 *   form (the friend-code chip) - show it whole (see `compactChipText`)
 * @param {Function} p.onClick
 */
export const UpgradePill = React.memo(function UpgradePill({
  variant,
  label,
  hasOffer = false,
  compact = false,
  compactWhole = false,
  onClick,
}) {
  const short = compact ? compactChipText(label, compactWhole) : "";
  // An empty slice means there is nothing to show - the caller should not have
  // asked for a compact form, and an empty chip is worse than no chip.
  if (compact && !short) return null;
  const painted = (
    <Box
      data-chip-paint=""
      data-testid={compact ? "navbar-upgrade-chip-paint" : undefined}
      sx={pillSx(variant, { hasOffer, compact })}
    >
      <Typography
        component="span"
        data-testid="navbar-upgrade-label"
        // THE LABEL'S OWN DIRECTION, and the one line this chip cannot lose.
        // The runs below are flex items, so their order on screen is the flex
        // container's `direction` - bidi reordering does not cross a flex item
        // boundary. The app shell declares no `dir`, so without this the
        // container resolved `ltr` and the Hebrew sentence was read back from
        // the wrong end ("ש"ח 59 שדרג - חיסכון עד"). It is taken from the
        // LABEL, not from a language flag or the ambient `dir`, so the chip is
        // correct in a container that has neither.
        //
        // It also fixes which end truncates: `text-overflow` clips at the
        // LOGICAL end, which for the Hebrew label is the left.
        dir={textDirection(label)}
        sx={{
          // A FLEX ROW of runs, so the prose can give way while the number
          // holds. `minWidth: 0` is load-bearing: without it this span is a
          // flex item at its default `min-width: auto`, which is its content
          // width, and nothing inside it can ever shrink no matter what
          // `text-overflow` says.
          display: "flex",
          alignItems: "baseline",
          minWidth: 0,
          fontSize: compact ? "13px" : "12.5px",
          fontWeight: compact ? 700 : 600,
          fontVariantNumeric: "normal",
          fontFamily: '"DM Sans", sans-serif',
          letterSpacing: "0.01em",
          lineHeight: 1,
          color: "inherit",
          whiteSpace: "nowrap",
          overflow: "hidden",
        }}
      >
        {compact ? short : <AmountLabel label={label} />}
      </Typography>
    </Box>
  );

  const interactive = {
    role: "button",
    tabIndex: 0,
    onClick,
    onKeyDown: (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onClick?.();
      }
    },
    "aria-label": label,
    "data-testid": compact
      ? "navbar-upgrade-chip-compact"
      : "navbar-upgrade-pill",
    "data-upsell-variant": variant,
    "data-has-offer": hasOffer ? "1" : "0",
  };

  // COMPACT: a transparent 44px hit area around a shorter painted pill, so the
  // touch target clears TAP_TARGET_MIN while the chip reads as a peer of the
  // hamburger beside it rather than as a block. Non-compact paints and handles
  // on the same box, as it always has.
  if (compact) {
    return (
      <Box {...interactive} sx={pillHitAreaSx()}>
        {painted}
      </Box>
    );
  }

  return React.cloneElement(painted, interactive);
});

/**
 * The same chip, shaped as a drawer row. A `MenuItem` so it keeps the menu's
 * own keyboard handling and closes it on select, skinned so it reads as a
 * row-shaped chip rather than a button bar across the menu.
 *
 * PASS-THROUGH. MUI's MenuList clones each of its direct children and injects
 * props into them - `autoFocus` on the row that takes focus when the menu
 * opens, `tabIndex` for the roving focus - so every prop this component does
 * not own (`...menuItemProps`) goes on to the MenuItem, and the ref is
 * forwarded to it. Without that the injected `autoFocus` was dropped, and a
 * menu whose first row was this one opened with nothing focused. The props
 * this component OWNS (click handler, test id, data attributes, skin) are set
 * after the spread, so a caller cannot replace them by accident.
 */
export const UpgradeDrawerRow = React.memo(React.forwardRef(function UpgradeDrawerRow({
  variant,
  label,
  hasOffer = false,
  onClick,
  ...menuItemProps
}, ref) {
  return (
    <MenuItem
      {...menuItemProps}
      ref={ref}
      onClick={onClick}
      data-testid="navbar-upgrade-drawer-row"
      data-upsell-variant={variant}
      data-has-offer={hasOffer ? "1" : "0"}
      sx={drawerRowSx(variant, { hasOffer })}
    >
      {/* The drawer row WRAPS instead of truncating. It has vertical room the
          toolbar chip does not, so there is no reason to drop a single
          character of the approved label here: at 14px the long form needs two
          lines inside a ~220px menu, and the row's 44px floor simply grows. */}
      <ListItemText
        primary={label}
        sx={{
          minWidth: 0,
          my: 0.5,
          "& .MuiTypography-root": {
            fontSize: "14px",
            fontWeight: 600,
            color: "inherit",
            whiteSpace: "normal",
            overflowWrap: "anywhere",
            lineHeight: 1.35,
          },
        }}
      />
    </MenuItem>
  );
}));
